/* eslint-disable complexity */
/* eslint-disable max-nested-callbacks */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axios';
import actionTypes from './actionTypes';
import { IBasketItem, IQuantityOperation } from './models';
import { RootState } from '../../store/store';
import { BasketItemType } from './models';
import { IProductComponents } from '../common/models/productModels';
import moment from 'moment';
import { normalizeLimit, inBasketQtyForProduct } from '../../utils/limits';
import {
  isCategoryStandaloneEligible,
  cartHasEligibleForSameProduct,
} from '../../utils/standalone';

export const addToBasket = createAsyncThunk(
  actionTypes.ADD_TO_BASKET,
  async (
    payload: IBasketItem | IBasketItem[],
    thunkApi
  ): Promise<[items: IBasketItem[], lastId: string]> => {
    const rootState = thunkApi.getState() as RootState;
    const state = rootState.basket;
    const items = structuredClone(state.items) || [];

    const toAdd: IBasketItem[] = Array.isArray(payload) ? payload : [payload];

    const accepted: IBasketItem[] = [];

    for (const basketItem of toAdd) {
      const dt = basketItem.date ?? basketItem.startDate ?? null;
      if (dt) {
        const itemDate = moment(dt);
        if (itemDate.isBefore(moment().startOf('day'))) {
          continue;
        }
      }

      const cat = basketItem.item?.consumerCategories?.find(
        (c: any) => c.id === basketItem.consumerCategoryId
      );
      const addIsEligible = isCategoryStandaloneEligible(cat);

      const poolForCheck = [...items, ...accepted];
      const hasEligibleSameProduct = cartHasEligibleForSameProduct(
        poolForCheck,
        basketItem.item
      );

      if (!addIsEligible && !hasEligibleSameProduct) {
        continue;
      }

      const limit = normalizeLimit(
        (basketItem.item as any)?.settings?.maximumQuantity
      );
      if (limit != null) {
        const currentSum = inBasketQtyForProduct(poolForCheck, basketItem.item);
        const remaining = Math.max(0, limit - currentSum);
        if (remaining <= 0) {
          continue;
        }
        basketItem.quantity = Math.min(basketItem.quantity, remaining);
        if (basketItem.quantity <= 0) continue;
      }

      const {
        item,
        itemType,
        date,
        startDate,
        endDate,
        adultCount,
        childrenAges,
        consumerCategoryId,
        licencePlates,
        name,
        selectedHour,
      } = basketItem;

      const id = `${item.id}_${itemType}_${date || startDate || ''}_${(
        licencePlates || []
      ).join('_')}_${consumerCategoryId}_${adultCount ?? ''}_${(
        childrenAges || []
      ).join('_')}`;

      const existing = items.find((x) => {
        if (x.itemType === BasketItemType.SkiPass) {
          return x.id === id;
        }
        return (
          x.id === id &&
          (x.date === date ||
            (x.startDate === startDate && x.endDate === endDate)) &&
          x.adultCount === adultCount &&
          (x.childrenAges?.length || 0) === (childrenAges?.length || 0) &&
          x.name === name &&
          JSON.stringify(
            x.productComponents?.map((pc: IProductComponents) =>
              pc?.productAdditionalSettings?.map((it) => it?.value)
            ) ?? []
          ) === JSON.stringify(selectedHour ?? [])
        );
      });

      if (existing) {
        existing.quantity += basketItem.quantity;
      } else {
        basketItem.id = id;
        items.push(basketItem);
      }
      accepted.push(basketItem);
    }

    if (accepted.length === 0) {
      return [items, ''];
    }

    try {
      const data = JSON.stringify(items);
      const response = await axiosInstance.put<IBasketItem[]>(
        '/v1/order/basket/recalculate',
        data
      );
      const lastId = (accepted[accepted.length - 1].id as string) ?? '';
      return [response.data, lastId];
    } catch (e) {
      console.error('Error while recalculating', JSON.stringify(e));
      const lastId = (accepted[accepted.length - 1].id as string) ?? '';
      return [items, lastId];
    }
  }
);

export const removeBasketItem = createAsyncThunk(
  actionTypes.REMOVE_FROM_BASKET,
  async (
    basketItemId: string,
    thunkApi
  ): Promise<[items: IBasketItem[], removed: boolean]> => {
    const rootState = thunkApi.getState() as RootState;
    const state = rootState.basket;
    const items = structuredClone(state.items);

    const itemIndex = items.findIndex((x) => x.id === basketItemId);
    if (itemIndex < 0) {
      console.warn('Item doesn`t exist in basket.');
      return [items, false];
    }
    items.splice(itemIndex, 1);

    try {
      const data = JSON.stringify(items);
      const response = await axiosInstance.put<IBasketItem[]>(
        '/v1/order/basket/recalculate',
        data
      );

      return [response.data, true];
    } catch (e) {
      console.error('Error while recalculating', JSON.stringify(e));
      //toast.error(i18n.t('common:error_message'));
      return [items, true];
    }
  }
);

export const addQuantityToBasketItem = createAsyncThunk(
  actionTypes.ADD_QUANTITY_BASKET_ITEM,
  async (
    quantityOperation: IQuantityOperation,
    thunkApi
  ): Promise<[items: IBasketItem[], changed: boolean]> => {
    const rootState = thunkApi.getState() as RootState;
    const state = rootState.basket;
    const items = structuredClone(state.items);

    const item = items.find((x) => x.id === quantityOperation.basketId);

    if (!item) {
      console.warn('Item doesn`t exist in basket.');
      return [items, false];
    }

    item.quantity += quantityOperation.changeValueBy;
    if (item.quantity <= 0) {
      items.splice(
        items.findIndex((x) => x.id === quantityOperation.basketId),
        1
      );
    }

    try {
      const data = JSON.stringify(items);
      const response = await axiosInstance.put<IBasketItem[]>(
        '/v1/order/basket/recalculate',
        data
      );

      return [response.data, true];
    } catch (e) {
      console.error('Error while recalculating', JSON.stringify(e));
      //toast.error(i18n.t('common:error_message'));
      return [items, true];
    }
  }
);
